import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import { Menu, X, Languages } from "lucide-react";

const NAV_LINKS = [
  { to: "/products", key: "nav.products", id: "products" },
  { to: "/services", key: "nav.services", id: "services" },
  { to: "/projects", key: "nav.projects", id: "projects" },
  { to: "/about", key: "nav.about", id: "about" },
  { to: "/contact", key: "nav.contact", id: "contact" },
];

export const Navbar = ({ transparentOnHome = true }) => {
  const { t, lang, toggleLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 24);
    handler();
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const isHome = pathname === "/";
  const useTransparent = transparentOnHome && isHome && !scrolled;

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        useTransparent
          ? "bg-transparent"
          : "backdrop-blur-xl bg-[#F9F8F6]/85 border-b border-[#DCD7D1]/60"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
        <Link
          to="/"
          className={`font-display text-xl tracking-tight ${useTransparent ? "text-white" : "text-[#1A1A1A]"}`}
          data-testid="brand-logo"
        >
          <span className="font-medium">Pick</span>
          <span className="text-[#8A5A44]"> · </span>
          <span className="font-light">Bodenbeläge</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.id}
              to={link.to}
              className={({ isActive }) =>
                `text-sm tracking-wide link-underline transition-colors ${
                  useTransparent ? "text-white/90 hover:text-white" : "text-[#1A1A1A]"
                } ${isActive ? "font-medium" : ""}`
              }
              data-testid={`nav-${link.id}-link`}
            >
              {t(link.key)}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={toggleLang}
            className={`hidden md:inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs font-medium uppercase tracking-[0.18em] transition-colors ${
              useTransparent
                ? "border border-white/40 text-white hover:bg-white hover:text-[#1A1A1A]"
                : "border border-[#1A1A1A]/20 text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-[#F9F8F6]"
            }`}
            data-testid="lang-toggle-btn"
            aria-label="Toggle language"
          >
            <Languages size={14} strokeWidth={1.5} />
            {lang === "de" ? "DE / EN" : "EN / DE"}
          </button>

          <Link
            to="/contact"
            className={`hidden md:inline-flex btn-primary text-sm ${
              useTransparent ? "" : ""
            }`}
            style={useTransparent ? { background: "#F9F8F6", color: "#1A1A1A" } : undefined}
            data-testid="nav-cta-btn"
          >
            {t("nav.cta")}
          </Link>

          <button
            className={`lg:hidden p-2 ${useTransparent ? "text-white" : "text-[#1A1A1A]"}`}
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#F9F8F6]/95 backdrop-blur-xl border-t border-[#DCD7D1]" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.id}
                to={link.to}
                className={({ isActive }) =>
                  `text-left font-display text-2xl ${isActive ? "text-[#8A5A44]" : "text-[#1A1A1A]"}`
                }
                data-testid={`mobile-nav-${link.id}-link`}
              >
                {t(link.key)}
              </NavLink>
            ))}
            <div className="flex items-center gap-3 pt-4 flex-wrap">
              <button
                onClick={toggleLang}
                className="px-3 py-2 rounded-full border border-[#1A1A1A]/20 text-xs font-medium uppercase tracking-[0.18em]"
                data-testid="mobile-lang-toggle-btn"
              >
                {lang === "de" ? "DE / EN" : "EN / DE"}
              </button>
              <Link to="/contact" className="btn-primary text-sm" data-testid="mobile-nav-cta-btn">
                {t("nav.cta")}
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
